import { isAbortError } from 'cadenza/api-client/abort-controller/abort-error';
import { deleteWorkOrders } from 'cadenza/api-client/workorder-api';
import { getStore } from 'cadenza/store/cadenza-store';
import { assertUnreachable } from 'cadenza/utils/custom-error';
import { logger } from 'cadenza/utils/logging';
import type { EnrichmentRequest } from 'cadenza/workbook/api/enrichment-api';
import { rerunEnrichment } from 'cadenza/workbook/api/enrichment-api';
import type { EnrichmentType } from 'cadenza/workbook/api/enrichment-type';
import { confirmSavePublishedWorkbookBeforeAction } from 'cadenza/workbook/confirm-save-and-execute-dialog/confirm-save-published-workbook';
import { showRerunEnrichmentDialog } from 'cadenza/workbook/enrichment/enrichment-dialog/create-enrichment-dialog';
import { showEnrichmentProgressDialog } from 'cadenza/workbook/enrichment/enrichment-progress-dialog';
import {
  createEnrichmentErrorAlert,
  openEnrichmentReportWorksheet,
} from 'cadenza/workbook/enrichment/enrichment-utils';
import type { EnrichmentDto, SpecificEnrichmentDto } from 'cadenza/workbook/enrichment/model/enrichment-dto';
import { createEnrichmentOptionsFromDtoParametrization } from 'cadenza/workbook/enrichment/rerun/rerun-enrichment-parametrization';
import {
  showRerunEnrichmentNotPossibleDialog,
  verifyAllResourcesForRerunAvailable,
} from 'cadenza/workbook/enrichment/rerun/rerun-enrichment-parametrization-verification';
import {
  fetchDataDependencyState,
  getObjecttypeById,
  getWorkbookNameAndHash,
  getWorkbookId,
  reloadObjecttype,
} from 'cadenza/workbook/workbook.state';

/**
 * please think twice before adding more parateters to this function.
 */
export async function executeRerun(enrichmentDto: EnrichmentDto) {
  // 1) if the workbook is under publishing control, then the workbook may have to be saved which the user has to confirm first
  const confirmation = await confirmSavePublishedWorkbookBeforeAction();
  if (!confirmation) {
    return;
  }

  // 2) is the enrichment rerunnable?
  const enrichmentRerunnableStatus = enrichmentDto.enrichmentRerunnableStatus;
  if (enrichmentRerunnableStatus.value === 'geometry-missing-error') {
    return showRerunEnrichmentNotPossibleDialog('enriched-objecttype-unavailable', 'enriched-attribute-unavailable');
  }

  // 3) are all objecttypes and attributes necessary for the rerun available?
  const allResourcesAvailable = await verifyAllResourcesForRerunAvailable(enrichmentDto);
  if (!allResourcesAvailable) {
    return;
  }

  // 4) user configures rerun parameters
  const enrichmentRequest = await connectAndShowRerunEnrichmentDialog(enrichmentDto);
  if (!enrichmentRequest) {
    return;
  }

  // 5) rerun the enrichment
  let enrichmentResult;
  try {
    enrichmentResult = await executeEnrichmentRerun(enrichmentDto, enrichmentRequest);
  } catch (error) {
    if (!isAbortError(error)) {
      logger.error('Failed to execute Enrichment', error);
      await createEnrichmentErrorAlert();
    }
    return;
  }

  // 6) reload the enriched objecttype
  await reloadObjecttype(enrichmentDto.enrichedObjecttypeId);
  await getStore().dispatch(fetchDataDependencyState());

  // 7) if available, navigate to report worksheet
  if (enrichmentResult.createdReportWorksheetId) {
    openEnrichmentReportWorksheet(enrichmentResult.createdReportWorksheetId);
  }
}

function createBaseOptions(enrichmentDto: EnrichmentDto) {
  const { enrichedObjecttypeId, associatedObjecttypeId } = enrichmentDto;
  const existingEnrichmentObjecttypeId = enrichmentDto.objecttypeId;
  const enrichmentObjecttype = getObjecttypeById(getStore().getState(), existingEnrichmentObjecttypeId);

  return {
    enrichedObjecttypeId,
    existingEnrichmentObjecttypeId,
    associatedBasicObjecttypeId: associatedObjecttypeId,
    groupAttributes: enrichmentDto.grouped,
    targetObjecttypeName: enrichmentObjecttype.printName,
    workbookId: getWorkbookId(getStore().getState()),
  };
}

async function connectAndShowRerunEnrichmentDialog(enrichmentDto: EnrichmentDto) {
  if (enrichmentDto.enrichmentType === 'AnalyticsExtensionEnrichment') {
    // special case for AnalyticsExtensions
    return await connectAndShowRerunEnrichmentDialogForAnalyticsExtension(enrichmentDto);
  }

  const baseOptions = createBaseOptions(enrichmentDto);

  // this switch is only necessary for typing. If we add new properties to the parametrization dtos,
  // we'll see an error on the options dto. This typechecking can't happen without narrowing the type
  // down to the specific enrichment type first.
  switch (enrichmentDto.enrichmentType) {
    case 'MeasuresFromSpatialIntersectionEnrichment': {
      const options = createEnrichmentOptionsFromDtoParametrization(enrichmentDto);
      return showRerunEnrichmentDialog(enrichmentDto.enrichmentType, { ...baseOptions, ...options });
    }
    case 'StructuredGeocodingEnrichment': {
      const options = createEnrichmentOptionsFromDtoParametrization(enrichmentDto);
      return showRerunEnrichmentDialog(enrichmentDto.enrichmentType, { ...baseOptions, ...options });
    }
    case 'UnstructuredGeocodingEnrichment': {
      const options = createEnrichmentOptionsFromDtoParametrization(enrichmentDto);
      return showRerunEnrichmentDialog(enrichmentDto.enrichmentType, { ...baseOptions, ...options });
    }
    case 'ReverseGeocodingEnrichment': {
      const options = createEnrichmentOptionsFromDtoParametrization(enrichmentDto);
      return showRerunEnrichmentDialog(enrichmentDto.enrichmentType, { ...baseOptions, ...options });
    }
    case 'IpGeocodingEnrichment': {
      const options = createEnrichmentOptionsFromDtoParametrization(enrichmentDto);
      return await showRerunEnrichmentDialog(enrichmentDto.enrichmentType, { ...baseOptions, ...options });
    }
    case 'IsochronesEnrichment': {
      const options = createEnrichmentOptionsFromDtoParametrization(enrichmentDto);
      return await showRerunEnrichmentDialog(enrichmentDto.enrichmentType, { ...baseOptions, ...options });
    }
    default:
      assertUnreachable(enrichmentDto);
  }
}

async function connectAndShowRerunEnrichmentDialogForAnalyticsExtension(
  enrichmentDto: SpecificEnrichmentDto<'AnalyticsExtensionEnrichment'>,
) {
  const baseOptions = createBaseOptions(enrichmentDto);
  const options = createEnrichmentOptionsFromDtoParametrization(enrichmentDto);
  return await showRerunEnrichmentDialog(enrichmentDto.enrichmentType, { ...baseOptions, ...options });
}

async function executeEnrichmentRerun(
  enrichmentDto: EnrichmentDto,
  enrichmentRequest: EnrichmentRequest<EnrichmentType>,
) {
  let workOrderId;
  try {
    const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
    const enrichedObjecttypeId = enrichmentDto.enrichedObjecttypeId;
    const enrichmentType = enrichmentDto.enrichmentType;
    workOrderId = await rerunEnrichment(workbookNameAndHash, enrichedObjecttypeId, enrichmentType, enrichmentRequest);
    return await showEnrichmentProgressDialog(enrichmentType, enrichedObjecttypeId, workOrderId);
  } finally {
    if (workOrderId) {
      await deleteWorkOrders([workOrderId]);
    }
  }
}
